// agenda.ts — reine, React-freie Helfer für die Kontakt-Termine-Agenda (SP7).
// Gruppierung nach Kalendertag, Uhrzeit-/Tages-Labels. Bewusst ohne DOM/JSX →
// per vitest testbar (src/**/*.test.ts). Datums-Parsing nutzt die bestehenden
// Graph-Helfer (UTC-ohne-Z bzw. Ganztags-Datumsteil), damit die Zeitzonen-
// Behandlung 1:1 zum restlichen Kalender bleibt.

import type { ContactTerminEvent } from "../../services/calendarService";
import { toGraphDate } from "../dashboard/DashboardShared";
import { graphAllDayDate } from "../calendar/allDay";
import { htmlZuKlartext } from "../vorgang/v4/klartext";

export interface TerminDay {
    /** Lokaler Kalendertag als "YYYY-MM-DD" (Gruppierungsschlüssel). */
    dayKey: string;
    /** Lokale Mitternacht des Tages (für Überschrift/Sortierung). */
    date: Date;
    events: ContactTerminEvent[];
}

/**
 * Lokaler Start-Zeitpunkt eines Termins. Ganztagstermine zählen nur mit ihrem
 * Datumsteil (als lokale Mitternacht), getaktete Termine werden aus dem
 * UTC-Graph-String korrekt in die lokale Zone gebracht.
 */
export function terminStartDate(ev: ContactTerminEvent): Date {
    return ev.isAllDay ? graphAllDayDate(ev.start.dateTime) : toGraphDate(ev.start.dateTime);
}

const dayKeyOf = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

/**
 * Gruppiert Termine nach lokalem Kalendertag. Tage ABSTEIGEND — der späteste
 * Tag steht oben (T-308, 13.10.2026: „drehen – neueste immer oben"). Innerhalb
 * eines Tages bleibt die Reihenfolge des Kalenders: chronologisch nach
 * absolutem Start. Termine mit unlesbarem Datum entfallen.
 */
export function groupTermineByDay(events: ContactTerminEvent[]): TerminDay[] {
    const byKey = new Map<string, TerminDay>();
    for (const ev of events) {
        const start = terminStartDate(ev);
        if (Number.isNaN(start.getTime())) continue;
        const dayStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const key = dayKeyOf(dayStart);
        let bucket = byKey.get(key);
        if (!bucket) {
            bucket = { dayKey: key, date: dayStart, events: [] };
            byKey.set(key, bucket);
        }
        bucket.events.push(ev);
    }
    const days = Array.from(byKey.values()).sort((a, b) => b.date.getTime() - a.date.getTime());
    for (const d of days) {
        d.events.sort((a, b) => terminStartDate(a).getTime() - terminStartDate(b).getTime());
    }
    return days;
}

/** So lang darf der Notiz-Auszug in der zugeklappten Zeile höchstens werden. */
export const NOTIZ_AUSZUG_ZEICHEN = 160;

/**
 * Kurzer Klartext der Notiz-Seite eines Termins für die zugeklappte Zeile
 * (T-309, 13.10.2026, PO: „auch auf der Ebene anzeigen"). Gekürzt am
 * Wortende; eine Seite nur mit Zeichnung wird benannt wie im Vorgang.
 * `null`, wenn der Termin keine Notiz hat.
 */
export function notizAuszug(ev: ContactTerminEvent, max = NOTIZ_AUSZUG_ZEICHEN): string | null {
    const notiz = ev.kmvs?.notiz;
    if (!notiz) return null;
    const text = htmlZuKlartext(notiz.contentHtml ?? "");
    if (!text) return notiz.hatZeichnung ? "Handschriftliche Notiz (Zeichnung)" : null;
    if (text.length <= max) return text;
    const schnitt = text.slice(0, max);
    const luecke = schnitt.lastIndexOf(" ");
    return `${(luecke > max * 0.6 ? schnitt.slice(0, luecke) : schnitt).trimEnd()} …`;
}

/** Uhrzeit-Label eines Termins: „Ganztägig" oder „09:00 – 10:00". */
export function formatTerminTime(ev: ContactTerminEvent): string {
    if (ev.isAllDay) return "Ganztägig";
    const fmt = (dt: string) => {
        const d = toGraphDate(dt);
        return Number.isNaN(d.getTime())
            ? ""
            : d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    };
    const s = fmt(ev.start.dateTime);
    const e = fmt(ev.end.dateTime);
    if (s && e) return `${s} – ${e}`;
    return s || e || "";
}

/** Tages-Überschrift: „Heute · Mo, 06.07.2026" bzw. „Morgen"/„Gestern"-Präfix. */
export function formatDayHeading(date: Date, today: Date = new Date()): string {
    const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const diffDays = Math.round((date.getTime() - t0.getTime()) / 86_400_000);
    const weekday = date.toLocaleDateString("de-DE", { weekday: "short" });
    const datum = date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
    const base = `${weekday}, ${datum}`;
    if (diffDays === 0) return `Heute · ${base}`;
    if (diffDays === 1) return `Morgen · ${base}`;
    if (diffDays === -1) return `Gestern · ${base}`;
    return base;
}

/** Liegt der Tag (lokale Mitternacht) vor dem heutigen Tag? */
export function istVergangen(date: Date, today: Date = new Date()): boolean {
    const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return date.getTime() < t0.getTime();
}

// ── Stufenweises Laden (PO 28.09.2026) ─────────────────────────────────────
// Der Reiter zeigt ALLE Termine von 3 Jahren zurück bis 2 Jahre voraus. Ein
// einziger Scan über fünf Jahre bräche an der 30-Sekunden-Grenze des
// DEV-Proxys ab; deshalb erst das nahe Fenster (−30/+90 Tage, sofort
// sichtbar), dann Halbjahr für Halbjahr nach außen.

export const NAH_ZURUECK_TAGE = 30;
export const NAH_VOR_TAGE = 90;
export const MAX_ZURUECK_MONATE = 36;
export const MAX_VOR_MONATE = 24;
export const STUECK_MONATE = 6;

export interface LadeStueck {
    from: Date;
    to: Date;
    richtung: "nah" | "zurueck" | "vor";
}

const tagVerschoben = (heute: Date, tage: number, tagesende = false): Date => {
    const d = new Date(heute.getFullYear(), heute.getMonth(), heute.getDate() + tage);
    if (tagesende) d.setHours(23, 59, 59, 999);
    return d;
};

const monateVerschoben = (heute: Date, monate: number, tagesende = false): Date => {
    const d = new Date(heute.getFullYear(), heute.getMonth() + monate, heute.getDate());
    if (tagesende) d.setHours(23, 59, 59, 999);
    return d;
};

/**
 * Der Ladeplan: zuerst das nahe Fenster, dann die Halbjahre abwechselnd
 * rückwärts und vorwärts (rückwärts zuerst — die Historie ist im Reiter die
 * häufigere Frage), bis −3 bzw. +2 Jahre erreicht sind. Die Stücke grenzen
 * lückenlos aneinander; ein Termin über eine Grenze kommt zweimal und wird
 * beim Mischen entdoppelt.
 */
export function ladePlan(heute: Date = new Date()): LadeStueck[] {
    const nah: LadeStueck = {
        from: tagVerschoben(heute, -NAH_ZURUECK_TAGE),
        to: tagVerschoben(heute, NAH_VOR_TAGE, true),
        richtung: "nah",
    };
    const grenzeZurueck = monateVerschoben(heute, -MAX_ZURUECK_MONATE);
    const grenzeVor = monateVerschoben(heute, MAX_VOR_MONATE, true);

    const zurueck: LadeStueck[] = [];
    for (let bis = nah.from; bis > grenzeZurueck; ) {
        const von = new Date(bis.getFullYear(), bis.getMonth() - STUECK_MONATE, bis.getDate());
        const from = von < grenzeZurueck ? grenzeZurueck : von;
        zurueck.push({ from, to: bis, richtung: "zurueck" });
        bis = from;
    }
    const vor: LadeStueck[] = [];
    for (let von = nah.to; von < grenzeVor; ) {
        const bis = new Date(von.getFullYear(), von.getMonth() + STUECK_MONATE, von.getDate(), 23, 59, 59, 999);
        const to = bis > grenzeVor ? grenzeVor : bis;
        vor.push({ from: von, to, richtung: "vor" });
        von = to;
    }

    const plan: LadeStueck[] = [nah];
    for (let i = 0; i < Math.max(zurueck.length, vor.length); i++) {
        if (zurueck[i]) plan.push(zurueck[i]);
        if (vor[i]) plan.push(vor[i]);
    }
    return plan;
}

/** Derselbe Schlüssel wie im Backend (`subject|start|end|organizer`). */
export function terminSchluessel(ev: ContactTerminEvent): string {
    return `${ev.subject}|${ev.start?.dateTime ?? ""}|${ev.end?.dateTime ?? ""}|${ev.organizer?.address ?? ""}`;
}

/**
 * Neue Termine in die vorhandene Liste mischen: entdoppelt nach
 * {@link terminSchluessel}, chronologisch. Steht ein Termin schon da, bleibt
 * er — nur ein `zugewiesen` und ein „eigener" (`via: null`) setzen sich wie im
 * Backend durch.
 */
export function mischeTermine(
    vorhanden: ContactTerminEvent[],
    neu: ContactTerminEvent[],
): ContactTerminEvent[] {
    const byKey = new Map<string, ContactTerminEvent>();
    for (const ev of vorhanden) byKey.set(terminSchluessel(ev), ev);
    for (const ev of neu) {
        const k = terminSchluessel(ev);
        const alt = byKey.get(k);
        if (!alt) {
            byKey.set(k, ev);
            continue;
        }
        const zugewiesen = !!(alt.zugewiesen || ev.zugewiesen);
        const via = alt.via === null || ev.via === null || (!alt.via && !ev.via) ? null : alt.via ?? ev.via;
        if (zugewiesen !== !!alt.zugewiesen || via !== alt.via) {
            byKey.set(k, { ...alt, zugewiesen, via });
        }
    }
    return Array.from(byKey.values()).sort(
        (a, b) => terminStartDate(a).getTime() - terminStartDate(b).getTime(),
    );
}
